import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { Button, Dialog, DialogText, cx, useToast } from "@amber/ui";
import type { EvidenceRecord, SpeakerView } from "../../contracts/index";
import type { Editing } from "../editing/Editing";
import { laneColor } from "../format";
import type { PlayerState } from "./Player";
import { TimingReview } from "./TimingReview";
import { TurnInspector } from "./TimingInspector";
import {
  describe,
  draftErrors,
  exact,
  fromTurns,
  mergePair,
  moveWord as movedWord,
  redoStack,
  replacePair,
  same,
  splitDraft,
  undoStack,
  withEdge,
  type Draft,
  type DraftStack,
  type Edge,
} from "./timingDraft";

const ZOOMS = [1, 4, 8, 16] as const;
const SNAP_PX = 8;

type Zoom = (typeof ZOOMS)[number];
type MergeDirection = "previous" | "next";
type DragStart = (e: ReactPointerEvent<HTMLSpanElement>, index: number, edge: Edge) => void;

interface TimingLane {
  speaker: SpeakerView | null;
  id: string | null;
}

const KEY_ACTION: Record<string, "split" | "merge" | "exit"> = {
  s: "split",
  S: "split",
  m: "merge",
  M: "merge",
  t: "exit",
  T: "exit",
};

function useTimingDrafts(record: EvidenceRecord, player: PlayerState, selected: number | null) {
  const toast = useToast();
  const selectedTurn = record.turns.find((t) => t.idx === selected);
  const communication =
    record.communications.find((c) => c.ordinal === (selectedTurn?.communication ?? 1)) ?? record.communications[0];
  const baseTurns = useMemo(
    () =>
      record.turns
        .filter((t) => t.communication === (communication?.ordinal ?? 1))
        .sort((a, b) => a.start_ms - b.start_ms),
    [record.turns, communication],
  );
  const base = useMemo(() => fromTurns(baseTurns), [baseTurns]);
  const [baseRevision] = useState(communication?.version?.revision_id ?? "");
  const [stack, setStack] = useState<DraftStack>(() => ({ past: [], now: base, future: [] }));
  const drafts = stack.now;
  const [current, setCurrent] = useState(() =>
    Math.max(
      0,
      base.findIndex((d) => d.key === selectedTurn?.unit_id),
    ),
  );
  const [zoom, setZoom] = useState<Zoom>(4);
  const [review, setReview] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [mergeChoice, setMergeChoice] = useState<MergeDirection | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const total = Math.max(player.durationMs, record.key_facts.clip.duration_ms ?? 0, base.at(-1)?.end ?? 0, 1);
  const changed = !same(base, drafts);
  const d = drafts[Math.min(current, drafts.length - 1)];

  useEffect(() => {
    const el = scroller.current;
    if (!el || !d) return;
    el.scrollLeft = (d.start / total) * el.scrollWidth - el.clientWidth / 3;
  }, [zoom, d?.key]);

  const commit = useCallback((next: Draft[]) => {
    setStack((s) => ({ past: [...s.past, s.now], now: next, future: [] }));
  }, []);
  const undo = useCallback(() => {
    setStack(undoStack);
  }, []);
  const redo = useCallback(() => {
    setStack(redoStack);
  }, []);
  const errors = useMemo(() => draftErrors(drafts, total), [drafts, total]);
  const setEdge = useCallback(
    (index: number, edge: Edge, ms: number, alone: boolean, base0: Draft[] = drafts) =>
      withEdge(base0, { index, edge, ms, alone }, total),
    [drafts, total],
  );
  const split = useCallback(() => {
    if (!d) return;
    const result = splitDraft(d, Math.round(player.timeMs));
    if ("error" in result) {
      toast.show(result.error);
      return;
    }
    commit(drafts.flatMap((x) => (x === d ? [result.first, result.second] : [x])));
  }, [commit, d, drafts, player.timeMs, toast]);

  const moveWord = (direction: "give" | "take") => {
    const next = movedWord(drafts, d, direction);
    if (next) commit(next);
  };

  const merge = (direction: MergeDirection, speaker?: string | null) => {
    const i = drafts.indexOf(d as Draft);
    const j = direction === "next" ? i + 1 : i - 1;
    const [a, b] = direction === "next" ? [drafts[i], drafts[j]] : [drafts[j], drafts[i]];
    if (!a || !b) return;
    if (a.speaker !== b.speaker && speaker === undefined) {
      setMergeChoice(direction);
      return;
    }
    const merged = mergePair(a, b, speaker, base);
    if (!merged) {
      toast.show("Merge whole turns only; undo the split first");
      return;
    }
    setMergeChoice(null);
    commit(replacePair(drafts, a, b, merged));
    setCurrent(Math.min(i, j));
  };

  return {
    communication,
    baseTurns,
    base,
    baseRevision,
    stack,
    setStack,
    drafts,
    setCurrent,
    zoom,
    setZoom,
    review,
    setReview,
    leaving,
    setLeaving,
    mergeChoice,
    setMergeChoice,
    scroller,
    track,
    total,
    changed,
    d,
    commit,
    undo,
    redo,
    errors,
    setEdge,
    split,
    moveWord,
    merge,
  };
}

type TimingDrafts = ReturnType<typeof useTimingDrafts>;

function isEdgeNudge(e: KeyboardEvent): boolean {
  return (e.altKey || e.shiftKey) && (e.key === "ArrowLeft" || e.key === "ArrowRight");
}

function useTimingKeys(t: TimingDrafts, requestExit: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [role=dialog]")) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) t.redo();
        else t.undo();
        return;
      }
      if (e.metaKey || e.ctrlKey) return;
      const d = t.d;
      if (isEdgeNudge(e) && d) {
        e.preventDefault();
        const edge = e.altKey ? "start" : "end";
        t.commit(t.setEdge(t.drafts.indexOf(d), edge, d[edge] + (e.key === "ArrowRight" ? 100 : -100), false));
        return;
      }
      const action = KEY_ACTION[e.key];
      if (action === "split") t.split();
      else if (action === "merge") t.merge("next");
      else if (action === "exit") requestExit();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  });
}

function useEdgeDrag(t: TimingDrafts, player: PlayerState, gaps: EvidenceRecord["gaps"]) {
  const { drafts, total, track, setEdge, setStack } = t;
  const edges = useMemo(
    () => [...new Set([...drafts.flatMap((x) => [x.start, x.end]), ...gaps.flatMap((g) => [g.start_ms, g.end_ms])])],
    [drafts, gaps],
  );
  const [tip, setTip] = useState<{ ms: number; x: number } | null>(null);
  const drag: DragStart = (e, index, edge) => {
    e.preventDefault();
    e.stopPropagation();
    const el = track.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const msPerPx = total / rect.width;
    const startDrafts = drafts;
    let latest = drafts;
    const own = drafts[index]?.[edge];
    const move = (ev: PointerEvent) => {
      let ms = ((ev.clientX - rect.left) / rect.width) * total;
      const snaps = [player.timeMs, ...edges.filter((x) => x !== own)];
      const near = snaps.find((x) => Math.abs(x - ms) <= SNAP_PX * msPerPx);
      if (near !== undefined) ms = near;
      latest = setEdge(index, edge, ms, ev.altKey, startDrafts);
      setStack((s) => ({ ...s, now: latest }));
      setTip({ ms, x: ev.clientX - rect.left });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setTip(null);
      setStack((s) => ({ past: [...s.past, startDrafts], now: latest, future: [] }));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return { tip, drag };
}

function changeSummary(base: readonly Draft[], drafts: readonly Draft[], changed: boolean): string {
  if (!changed) return "";
  const count = describe(base, drafts).length;
  return ` · ${String(count)} change${count === 1 ? "" : "s"}`;
}

function TimingHeader({
  summary,
  zoom,
  setZoom,
  onDone,
}: {
  summary: string;
  zoom: Zoom;
  setZoom: (zoom: Zoom) => void;
  onDone: () => void;
}) {
  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-2">
      <h3 id="timing-heading" className="m-0 text-xs font-semibold tracking-[0.06em] text-muted uppercase">
        Adjust timing
      </h3>
      <span className="rounded-full border border-waiting px-2 text-md">
        Timing mode
        {summary}
      </span>
      <div role="group" aria-label="Zoom" className="ml-auto inline-flex rounded-md border border-divider p-0.5">
        {ZOOMS.map((z) => (
          <button
            key={z}
            type="button"
            aria-pressed={zoom === z}
            onClick={() => {
              setZoom(z);
            }}
            className={cx("h-6 rounded-[5px] px-2 text-md", zoom === z ? "bg-surface-3 font-semibold" : "text-muted")}
          >
            {z === 1 ? "Fit" : `${String(z)}×`}
          </button>
        ))}
      </div>
      <Button
        size="sm"
        onClick={() => {
          onDone();
        }}
      >
        Done
      </Button>
    </div>
  );
}

interface SegmentProps {
  x: Draft;
  i: number;
  lane: TimingLane;
  current: boolean;
  unchanged: boolean;
  total: number;
  pct: (ms: number) => string;
  label: string;
  onPick: (i: number, x: Draft) => void;
  onDragStart: DragStart;
}

function TimingSegment({ x, i, lane, current, unchanged, total, pct, label, onPick, onDragStart }: SegmentProps) {
  return (
    <div
      className={cx(
        "absolute top-[7px] h-3.5 rounded-[3px]",
        laneColor(lane.speaker?.lane ?? 0, !lane.speaker?.name),
        current && "ring-2 ring-fg",
        unchanged ? "" : "outline-2 outline-offset-1 outline-dashed outline-waiting",
      )}
      style={{ left: pct(x.start), width: pct(Math.max(x.end - x.start, 100)) }}
    >
      <button
        type="button"
        aria-label={`${label}, turn ${String(i + 1)}, ${exact(x.start)} to ${exact(x.end)}`}
        className="absolute inset-0 cursor-pointer"
        onClick={() => {
          onPick(i, x);
        }}
      />
      <span
        role="slider"
        tabIndex={-1}
        aria-label={`Start of turn ${String(i + 1)}`}
        aria-valuenow={x.start}
        aria-valuemin={0}
        aria-valuemax={total}
        onPointerDown={(e) => {
          onDragStart(e, i, "start");
        }}
        className="absolute inset-y-[-3px] left-[-3px] w-[6px] cursor-ew-resize rounded-sm bg-fg/70"
      />
      <span
        role="slider"
        tabIndex={-1}
        aria-label={`End of turn ${String(i + 1)}`}
        aria-valuenow={x.end}
        aria-valuemin={0}
        aria-valuemax={total}
        onPointerDown={(e) => {
          onDragStart(e, i, "end");
        }}
        className="absolute inset-y-[-3px] right-[-3px] w-[6px] cursor-ew-resize rounded-sm bg-fg/70"
      />
    </div>
  );
}

interface TimingTracksProps {
  lanes: readonly TimingLane[];
  drafts: readonly Draft[];
  base: readonly Draft[];
  index: number;
  zoom: Zoom;
  total: number;
  scroller: RefObject<HTMLDivElement | null>;
  track: RefObject<HTMLDivElement | null>;
  playheadMs: number;
  tip: { ms: number; x: number } | null;
  speakerName: (id: string | null) => string;
  onPick: (i: number, x: Draft) => void;
  onDragStart: DragStart;
}

function TimingTracks({ lanes, drafts, base, index, zoom, total, scroller, track, tip, ...rest }: TimingTracksProps) {
  const pct = (ms: number) => `${String((ms / total) * 100)}%`;
  const activity = drafts.flatMap((x) => x.wordTimes);
  return (
    <div className="flex">
      <ul className="m-0 w-[150px] flex-none list-none p-0">
        <li className="flex h-[22px] items-center pr-2 text-xs text-muted">Speech activity</li>
        {lanes.map((l) => (
          <li key={l.id ?? "none"} className="flex h-[29px] items-center truncate pr-2 text-ui font-strong">
            {l.speaker?.display ?? "Unattributed"}
          </li>
        ))}
      </ul>
      <div ref={scroller} className="min-w-0 flex-1 overflow-x-auto">
        <div ref={track} className="relative select-none" style={{ width: `${String(zoom * 100)}%` }}>
          <div
            className="relative h-[22px] border-b border-divider/60"
            aria-hidden="true"
            title="Speech activity from word timings"
          >
            {activity.map(([s, e], i) => (
              <span
                key={String(i)}
                className="absolute bottom-[3px] h-3 bg-muted/40"
                style={{ left: pct(s), width: pct(Math.max(e - s, 40)) }}
              />
            ))}
          </div>
          {lanes.map((l) => (
            <div key={l.id ?? "none"} className="relative h-[29px] border-b border-divider/60">
              <div className="absolute inset-x-0 top-[7px] h-3.5 rounded-sm bg-surface-2" />
              {drafts.map((x, i) =>
                x.speaker !== l.id ? null : (
                  <TimingSegment
                    key={x.key}
                    x={x}
                    i={i}
                    lane={l}
                    current={i === index}
                    unchanged={base.some((b) => b.key === x.key && b.start === x.start && b.end === x.end)}
                    total={total}
                    pct={pct}
                    label={rest.speakerName(x.speaker)}
                    onPick={rest.onPick}
                    onDragStart={rest.onDragStart}
                  />
                ),
              )}
            </div>
          ))}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 w-0.5 bg-fg"
            style={{ left: pct(rest.playheadMs) }}
          />
          {tip ? (
            <span
              className="pointer-events-none absolute top-0 -translate-x-1/2 rounded bg-fg px-1 font-mono text-2xs text-bg"
              style={{ left: tip.x }}
            >
              {exact(tip.ms)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function LeaveDialog({ count, onKeep, onDiscard }: { count: number; onKeep: () => void; onDiscard: () => void }) {
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onKeep();
      }}
      title="Discard timing changes?"
      onSubmit={onDiscard}
      footer={
        <>
          <Button
            onClick={() => {
              onKeep();
            }}
          >
            Keep editing
          </Button>
          <Button type="submit" variant="danger">
            Discard
          </Button>
        </>
      }
    >
      <DialogText>
        {count} unsaved change{count === 1 ? "" : "s"} will be lost.
      </DialogText>
    </Dialog>
  );
}

export function TimingMode({
  record,
  player,
  editing,
  selected,
  onSelect,
  onExit,
}: {
  record: EvidenceRecord;
  player: PlayerState;
  editing: Editing;
  selected: number | null;
  onSelect: (idx: number) => void;
  onExit: () => void;
}) {
  const t = useTimingDrafts(record, player, selected);
  const { base, drafts, d, changed, communication } = t;
  const requestExit = () => {
    if (changed) t.setLeaving(true);
    else onExit();
  };
  useTimingKeys(t, requestExit);
  const { tip, drag } = useEdgeDrag(t, player, record.gaps);
  const lanes = useMemo((): TimingLane[] => {
    const ids = [...new Set(drafts.map((x) => x.speaker))];
    return ids.map((id) => ({ speaker: record.speakers.find((s) => s.id === id) ?? null, id }));
  }, [drafts, record.speakers]);
  const index = d ? drafts.indexOf(d) : -1;
  const speakerName = (id: string | null) => record.speakers.find((s) => s.id === id)?.display ?? "Unattributed";

  return (
    <section aria-labelledby="timing-heading" className="border-t border-divider px-3 pt-2 pb-2">
      <TimingHeader
        summary={changeSummary(base, drafts, changed)}
        zoom={t.zoom}
        setZoom={t.setZoom}
        onDone={requestExit}
      />
      <TimingTracks
        lanes={lanes}
        drafts={drafts}
        base={base}
        index={index}
        zoom={t.zoom}
        total={t.total}
        scroller={t.scroller}
        track={t.track}
        playheadMs={player.timeMs}
        tip={tip}
        speakerName={speakerName}
        onPick={(i, x) => {
          t.setCurrent(i);
          player.seek(x.start);
          const turn = t.baseTurns.find((b) => b.unit_id === x.sources[0]);
          if (turn) onSelect(turn.idx);
        }}
        onDragStart={(e, i, edge) => {
          t.setCurrent(i);
          drag(e, i, edge);
        }}
      />
      {d ? (
        <TurnInspector
          d={d}
          index={index}
          drafts={drafts}
          baseOf={base.find((b) => b.key === d.key)}
          errors={t.errors}
          mergeChoice={t.mergeChoice}
          canUndo={t.stack.past.length > 0}
          canRedo={t.stack.future.length > 0}
          changed={changed}
          player={player}
          speakerName={speakerName}
          setCurrent={t.setCurrent}
          setEdge={t.setEdge}
          commit={t.commit}
          moveWord={t.moveWord}
          merge={t.merge}
          setMergeChoice={t.setMergeChoice}
          split={t.split}
          undo={t.undo}
          redo={t.redo}
          onDiscard={() => {
            t.commit(base);
          }}
          onReview={() => {
            t.setReview(true);
          }}
        />
      ) : null}
      {t.review && communication ? (
        <TimingReview
          record={record}
          communicationId={communication.id}
          communicationOrdinal={communication.ordinal}
          baseRevision={t.baseRevision}
          base={base}
          drafts={drafts}
          editing={editing}
          onClose={() => {
            t.setReview(false);
          }}
          onSaved={onExit}
        />
      ) : null}
      {t.leaving ? (
        <LeaveDialog
          count={describe(base, drafts).length}
          onKeep={() => {
            t.setLeaving(false);
          }}
          onDiscard={onExit}
        />
      ) : null}
    </section>
  );
}
